/**
 * Hooks du parcours élève (docs/12).
 *
 * Toute la logique de données vit ici, jamais dans le JSX (docs/24 §3).
 *
 * ⚠️ **Aucun de ces hooks ne protège quoi que ce soit.** `state`, `lock_reason`
 * et `attempts_left` viennent du serveur, qui les recalcule à chaque lecture :
 * l'interface s'en sert pour griser un bouton et écrire un message juste, rien
 * de plus. Chaque leçon verrouillée à l'écran a sa garde d'API en face, et c'est
 * elle qui refuse (docs/07 §8).
 *
 * L'écran ne rejoue **jamais** la règle de déblocage. Il n'additionne pas les
 * exercices réussis, ne compare pas les positions, ne décide pas de la leçon
 * suivante : `next_lesson_id` est calculé côté serveur pour cette raison
 * précise — deux implémentations d'une même règle divergent, et c'est celle du
 * client qu'on ne relit jamais.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, expectNoContent, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { catalogKeys, learningKeys } from "@/shared/api/query-keys";

export type CoursePlayer = components["schemas"]["CoursePlayerResponse"];
export type LessonProgress = components["schemas"]["LessonProgressResponse"];
export type EnrollmentSummary = components["schemas"]["EnrollmentSummaryResponse"];
export type ExerciseState = components["schemas"]["ExerciseStateResponse"];
export type Attempt = components["schemas"]["AttemptResponse"];
export type AttemptResult = components["schemas"]["AttemptResultResponse"];
export type LessonState = LessonProgress["state"];

/**
 * Invalide tout le parcours après une écriture.
 *
 * Large **délibérément**. Rendre une copie peut débloquer une leçon, faire
 * avancer la progression du cours et changer la ligne de « mes cours » — et
 * savoir lesquelles sans redemander au serveur demanderait de rejouer la règle
 * de déblocage côté client. Le coût est une requête ; le prix de l'alternative
 * est une seconde implémentation de RM-01.
 *
 * Le catalogue est invalidé lui aussi : s'inscrire ouvre les leçons du cours,
 * donc change `is_readable` sur une fiche déjà en cache.
 */
function useAfterProgressChange() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: learningKeys.all });
    void queryClient.invalidateQueries({ queryKey: catalogKeys.allCourses() });
  };
}

/** « Mes cours » — les inscriptions ouvertes de l'élève connecté. */
export function useMyEnrollments(): UseQueryResult<EnrollmentSummary[], ApiError> {
  return useQuery({
    queryKey: learningKeys.enrollments(),
    queryFn: async () => {
      const page = await unwrap(api.GET("/api/v1/enrollments", {}));
      return page.data;
    },
  });
}

/**
 * Le plan d'un cours avec l'état de chaque leçon.
 *
 * `retry: false` : la réponse la plus fréquente pour un visiteur non inscrit est
 * un 404, et le réessayer trois fois retarde l'affichage du bouton
 * « s'inscrire » sans jamais changer le résultat.
 */
export function useCourseProgress(
  courseId: string | undefined,
): UseQueryResult<CoursePlayer, ApiError> {
  return useQuery({
    queryKey: learningKeys.player(courseId ?? ""),
    enabled: courseId !== undefined,
    retry: false,
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/courses/{course_id}/progress", {
          params: { path: { course_id: courseId ?? "" } },
        }),
      ),
  });
}

export function useEnrol(): UseMutationResult<CoursePlayer, ApiError, string> {
  const afterChange = useAfterProgressChange();
  return useMutation({
    mutationFn: (courseId: string) =>
      unwrap(api.POST("/api/v1/enrollments", { body: { course_id: courseId } })),
    onSuccess: afterChange,
  });
}

export function useDrop(): UseMutationResult<void, ApiError, string> {
  const afterChange = useAfterProgressChange();
  return useMutation({
    mutationFn: (enrollmentId: string) =>
      expectNoContent(
        api.DELETE("/api/v1/enrollments/{enrollment_id}", {
          params: { path: { enrollment_id: enrollmentId } },
        }),
      ),
    onSuccess: afterChange,
  });
}

interface LessonTarget {
  enrollmentId: string;
  lessonId: string;
}

/**
 * Marque une leçon comme lue — un geste **déclaratif** (docs/12 §5).
 *
 * Il est accepté même s'il reste un exercice bloquant à réussir : la leçon ne
 * devient terminée que lorsque les deux conditions sont réunies, et c'est le
 * serveur qui en décide. L'écran ne doit donc pas désactiver le bouton sur
 * « exercice non réussi » — il refuserait un geste légitime.
 */
export function useCompleteLesson(): UseMutationResult<CoursePlayer, ApiError, LessonTarget> {
  const afterChange = useAfterProgressChange();
  return useMutation({
    mutationFn: ({ enrollmentId, lessonId }: LessonTarget) =>
      unwrap(
        api.POST("/api/v1/enrollments/{enrollment_id}/lessons/{lesson_id}/complete", {
          params: { path: { enrollment_id: enrollmentId, lesson_id: lessonId } },
        }),
      ),
    onSuccess: afterChange,
  });
}

export function useOpenLesson(): UseMutationResult<CoursePlayer, ApiError, LessonTarget> {
  const afterChange = useAfterProgressChange();
  return useMutation({
    mutationFn: ({ enrollmentId, lessonId }: LessonTarget) =>
      unwrap(
        api.POST("/api/v1/enrollments/{enrollment_id}/lessons/{lesson_id}/open", {
          params: { path: { enrollment_id: enrollmentId, lesson_id: lessonId } },
        }),
      ),
    onSuccess: afterChange,
  });
}

/** Essais restants, meilleur score, correction en attente — sans rien consommer. */
export function useExerciseState(
  exerciseId: string | undefined,
): UseQueryResult<ExerciseState, ApiError> {
  return useQuery({
    queryKey: learningKeys.exercise(exerciseId ?? ""),
    enabled: exerciseId !== undefined,
    retry: false,
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/exercises/{exercise_id}/state", {
          params: { path: { exercise_id: exerciseId ?? "" } },
        }),
      ),
  });
}

export function useStartAttempt(): UseMutationResult<Attempt, ApiError, string> {
  const afterChange = useAfterProgressChange();
  return useMutation({
    mutationFn: (exerciseId: string) =>
      unwrap(
        api.POST("/api/v1/exercises/{exercise_id}/attempts", {
          params: { path: { exercise_id: exerciseId } },
        }),
      ),
    onSuccess: afterChange,
  });
}

/**
 * Le brouillon de l'élève : une valeur de forme libre par question.
 *
 * `Record<string, Record<string, unknown>>` reproduit le contrat tel quel. La
 * **forme** de la valeur suit le type de la question, et c'est le serveur qui
 * sait la lire : la typer finement ici dupliquerait cette connaissance à
 * l'endroit qui ne corrige pas, et les deux définitions dériveraient au premier
 * type de question ajouté.
 */
export type DraftAnswers = Record<string, Record<string, unknown>>;

interface SaveAnswersInput {
  attemptId: string;
  answers: DraftAnswers;
}

export function useSaveAnswers(): UseMutationResult<Attempt, ApiError, SaveAnswersInput> {
  return useMutation({
    mutationFn: ({ attemptId, answers }: SaveAnswersInput) =>
      unwrap(
        api.PATCH("/api/v1/attempts/{attempt_id}/answers", {
          params: { path: { attempt_id: attemptId } },
          body: { answers },
        }),
      ),
  });
}

export function useSubmitAttempt(): UseMutationResult<AttemptResult, ApiError, string> {
  const afterChange = useAfterProgressChange();
  return useMutation({
    mutationFn: (attemptId: string) =>
      unwrap(
        api.POST("/api/v1/attempts/{attempt_id}/submit", {
          params: { path: { attempt_id: attemptId } },
        }),
      ),
    onSuccess: afterChange,
  });
}

/**
 * La leçon où l'élève doit reprendre, telle que **le serveur** l'a désignée.
 *
 * Un `find` sur `lessons` donnerait le même résultat aujourd'hui et un résultat
 * différent le jour où la règle changera. `next_lesson_id` existe pour que cette
 * question n'ait qu'une seule réponse.
 */
export function nextLesson(player: CoursePlayer): LessonProgress | undefined {
  return player.lessons.find((lesson) => lesson.lesson_id === player.next_lesson_id);
}
